import {type FormEvent, useState} from "react";
import {BaseAlert, BaseBadge, BaseBreadcrumb, BaseButton, BaseCard, BaseInput} from "@/components";
import {referralCodeSchema} from "./referralCode.schema";
import {useConnectReferralCode, useReferralLink, useReferralStats, useReferredUsers} from "./useReferrals";

const ReferralsPage = () => {
  const {link, code, isLoading: linkLoading, isError: linkError} = useReferralLink();
  const {stats, isLoading: statsLoading} = useReferralStats();
  const {users, isLoading: usersLoading, isError: usersError} = useReferredUsers();
  const connectMutation = useConnectReferralCode();

  const [referralInput, setReferralInput] = useState("");
  const [referralError, setReferralError] = useState<string | undefined>();
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied">("idle");

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus("copied");
      window.setTimeout(() => setCopyStatus("idle"), 2000);
    } catch {
      setCopyStatus("idle");
    }
  };

  const handleConnect = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = referralCodeSchema.safeParse({code: referralInput});
    if (!result.success) {
      setReferralError(result.error.issues[0]?.message ?? "Código inválido");
      return;
    }
    setReferralError(undefined);
    connectMutation.mutate(result.data.code, {
      onSuccess: () => setReferralInput(""),
    });
  };

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8">
      <BaseBreadcrumb items={[{label: "Referidos"}]} />
      <header className="flex flex-col gap-2">
        <h1 className="text-[var(--font-h1)] font-bold text-[var(--color-text)]">Programa de referidos</h1>
        <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Comparte tu enlace y gana por cada amigo que se registre.
        </p>
      </header>

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <div className="flex flex-col gap-3">
          <h2 className="text-[var(--font-h3)] font-semibold text-[var(--color-text)]">Tu enlace</h2>
          {linkLoading ? (
            <p className="text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando...</p>
          ) : linkError || !link ? (
            <p className="text-[var(--font-body-lg)] text-[var(--color-text-tertiary)]">
              Aún no tienes un enlace de referido disponible.
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2">
                <code className="flex-1 truncate text-[var(--font-body)] text-[var(--color-text)]">{link}</code>
                <BaseButton
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleCopy}
                >
                  {copyStatus === "copied" ? "Copiado" : "Copiar"}
                </BaseButton>
              </div>
              {code ? (
                <p className="text-[var(--font-body)] text-[var(--color-text-secondary)]">
                  Código: <strong className="text-[var(--color-text)]">{code}</strong>
                </p>
              ) : null}
            </>
          )}
        </div>
      </BaseCard>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <BaseCard
          variant="surface"
          padding="md"
        >
          <p className="text-[var(--font-body)] uppercase tracking-wider text-[var(--color-text-tertiary)]">
            Referidos
          </p>
          <p className="mt-1 text-[var(--font-h1)] font-bold text-[var(--color-text)]">
            {statsLoading ? "..." : stats.totalReferrals}
          </p>
        </BaseCard>
        <BaseCard
          variant="surface"
          padding="md"
        >
          <p className="text-[var(--font-body)] uppercase tracking-wider text-[var(--color-text-tertiary)]">
            Ganancias
          </p>
          <p className="mt-1 text-[var(--font-h1)] font-bold text-[var(--color-text)]">
            {statsLoading ? "..." : stats.totalEarned.toFixed(2)}
          </p>
        </BaseCard>
        <BaseCard
          variant="surface"
          padding="md"
        >
          <p className="text-[var(--font-body)] uppercase tracking-wider text-[var(--color-text-tertiary)]">
            Pendiente
          </p>
          <p className="mt-1 text-[var(--font-h1)] font-bold text-[var(--color-text)]">
            {statsLoading ? "..." : stats.pending.toFixed(2)}
          </p>
        </BaseCard>
      </div>

      <BaseCard
        variant="surface"
        padding="lg"
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={handleConnect}
          noValidate
        >
          <h2 className="text-[var(--font-h3)] font-semibold text-[var(--color-text)]">Ingresar código</h2>
          {connectMutation.isSuccess ? (
            <BaseAlert
              variant="success"
              title="Código aplicado"
            />
          ) : null}
          {connectMutation.isError ? (
            <BaseAlert
              variant="danger"
              title="No pudimos aplicar el código"
            >
              <p>{connectMutation.error?.message ?? "Verifica el código e inténtalo nuevamente."}</p>
            </BaseAlert>
          ) : null}
          <BaseInput
            label="Código de referido"
            placeholder="Ej: juan123"
            value={referralInput}
            onChange={e => {
              setReferralInput(e.target.value);
              setReferralError(undefined);
            }}
            error={referralError}
            disabled={connectMutation.isPending}
          />
          <BaseButton
            type="submit"
            variant="primary"
            disabled={connectMutation.isPending || !referralInput.trim()}
            fullWidth
          >
            {connectMutation.isPending ? "Aplicando..." : "Aplicar código"}
          </BaseButton>
        </form>
      </BaseCard>

      <div className="flex flex-col gap-3">
        <h2 className="text-[var(--font-h2)] font-semibold text-[var(--color-text)]">Tus referidos</h2>
        {usersLoading ? (
          <BaseCard
            variant="surface"
            padding="lg"
          >
            <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">Cargando...</p>
          </BaseCard>
        ) : usersError ? (
          <BaseAlert
            variant="danger"
            title="No pudimos cargar tus referidos"
          />
        ) : users.length === 0 ? (
          <BaseCard
            variant="surface"
            padding="lg"
          >
            <p className="text-center text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
              Aún no tienes referidos. ¡Comparte tu enlace!
            </p>
          </BaseCard>
        ) : (
          <ul className="flex flex-col gap-2">
            {users.map(user => (
              <li key={user.id}>
                <BaseCard
                  variant="surface"
                  padding="md"
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[var(--font-body-lg)] font-semibold text-[var(--color-text)]">
                      {user.fullName}
                    </p>
                    <p className="truncate text-[var(--font-body)] text-[var(--color-text-tertiary)]">{user.email}</p>
                  </div>
                  {user.status ? (
                    <BaseBadge
                      variant="neutral"
                      size="sm"
                    >
                      {user.status}
                    </BaseBadge>
                  ) : null}
                </BaseCard>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default ReferralsPage;
